import apiManager from '../lib/apiManager';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Get patient information by mobile number
 * @param {Object} params - Parameters
 * @param {number} params.UserId - User ID (required)
 * @param {string} params.Mobile - Mobile number (required)
 * @param {string} params.DateOfBirth - Date of birth (optional)
 * @param {string} params.UniqueId - Unique ID (optional)
 * @param {string} params.token - OTP verification token or device token (optional)
 * @param {boolean} params.isDeviceToken - Whether the token is a device token (default: false). If true, PreAuth is false; if false, PreAuth is true
 * @returns {Promise} API response with patient data array
 */
export const getPatient = async (params) => {
  try {
    const payload = {
      UserId: params.UserId,
      Mobile: params.Mobile,
      DateOfBirth: params.DateOfBirth || '',
      UniqueId: params.UniqueId || '',
      PreAuth: params.isDeviceToken ? false : true
    };

    // Create config with Authorization header if token is provided
    const config = {};
    if (params.token) {
      config.headers = {
        'Authorization': `Bearer ${params.token}`
      };
    }

    const response = await apiManager.post(`${API_BASE_URL}/Patient/GetPatient`, payload, config);


    return response.data;
  } catch (error) {
    console.error('Error fetching patient:', error);
    throw error;
  }
};

/**
 * Save a new patient record
 * @param {Object} params - Patient information
 * @param {number} params.UserId - User ID (required)
 * @param {string} params.Title - Title (e.g., Mr., Mrs., Ms., Dr.)
 * @param {string} params.FirstName - First name (required)
 * @param {string} params.LastName - Last name (required)
 * @param {string} params.Mobile - Mobile number (required)
 * @param {string} params.Gender - Gender (required)
 * @param {string} params.DateOfBirth - Date of birth (required, format: YYYY-MM-DD)
 * @param {string} params.NIC - National ID (optional)
 * @param {string} params.Address - Address (optional)
 * @param {string} params.BloodGroup - Blood group (optional)
 * @param {string} params.Occupation - Occupation (optional)
 * @param {string} params.MaritalStatus - Marital status (optional, 1-4 or string)
 * @param {string} params.Passport - Passport number (optional)
 * @param {string} params.token - Bearer token (OTP token for initial call, device token for authenticated calls)
 * @returns {Promise} API response with patient data
 */
export const savePatient = async (params) => {
  try {
    const payload = {
      UserId: params.UserId,
      Title: params.Title || '',
      FirstName: params.FirstName,
      LastName: params.LastName,
      Mobile: params.Mobile,
      Gender: params.Gender,
      DateOfBirth: params.DateOfBirth,
      NIC: params.NIC || '',
      Passport: params.Passport || '',
      BloodGroup: params.BloodGroup || '',
      Occupation: params.Occupation || '',
      MaritalStatus: params.MaritalStatus || null,
      Address: {
        Id: 0,
        AddressLine1: params.Address || ''
      },
      Id: 0,
      ParentId: null,
      PatientTypeId: null
    };

    // Create config with Authorization header if token is provided
    const config = {};
    if (params.token) {
      config.headers = {
        'Authorization': `Bearer ${params.token}`
      };
    }

    const response = await apiManager.post(`${API_BASE_URL}/Patient/Save`, payload, config);

    // Check for success response
    if (response.data?.resCode === 200 && response.data?.status === 1000) {
      return {
        success: true,
        message: response.data.message || 'Patient details saved successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data?.message || 'Failed to save patient',
        error: response.data?.error || 'SAVE_FAILED'
      };
    }
  } catch (error) {
    console.error('Error saving patient:', error);
    const errorMessage = error.response?.data?.message || 'Failed to save patient. Please try again.';
    const errorCode = error.response?.data?.error || 'NETWORK_ERROR';
    
    return {
      success: false,
      message: errorMessage,
      error: errorCode
    };
  }
};
/**
 * Update an existing patient record
 * @param {Object} params - Patient information (same as savePatient but with Id and AddressId)
 * @param {string} params.Id - Patient ID (required for update)
 * @param {number} params.AddressId - Existing address record ID (0 if new)
 * @returns {Promise} API response
 */
export const updatePatient = async (params) => {
  try {
    const payload = {
      UserId: params.UserId,
      Title: params.Title || '',
      FirstName: params.FirstName,
      LastName: params.LastName,
      Mobile: params.Mobile,
      Gender: params.Gender,
      DateOfBirth: params.DateOfBirth,
      NIC: params.NIC || '',
      Passport: params.Passport || '',
      BloodGroup: params.BloodGroup || '',
      Occupation: params.Occupation || '',
      MaritalStatus: params.MaritalStatus ? parseInt(params.MaritalStatus) : null,
      Address: {
        Id: params.AddressId || 0,
        AddressLine1: params.Address || ''
      },
      Id: params.Id,
      ParentId: null,
      PatientTypeId: null
    };

    const config = {};
    if (params.token) {
      config.headers = {
        'Authorization': `Bearer ${params.token}`
      };
    }

    const response = await apiManager.post(`${API_BASE_URL}/Patient/Save`, payload, config);

    if (response.data?.resCode === 200 && response.data?.status === 1000) {
      return {
        success: true,
        message: response.data.message || 'Patient details updated successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data?.message || 'Failed to update patient',
        error: response.data?.error || 'UPDATE_FAILED'
      };
    }
  } catch (error) {
    console.error('Error updating patient:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update patient. Please try again.',
      error: error.response?.data?.error || 'NETWORK_ERROR'
    };
  }
};